import { useState, useMemo, useCallback } from "react";
import {
  ColumnOption,
  MetricAggregation,
  MetricDisplayStyle,
  VivoPlotProps,
  plotTypes,
} from "@/components/VivoPlots/types";
import { v4 as uuidv4 } from "uuid";
import { detectFilterConflicts } from "./utils/filterConflictUtils";
import { getEditorBarplotSortBy } from "./utils/barplotSorting";

const RENDERED_AXIS_SORT_VERSION = 2;

export const createNewPlotConfig = (): VivoPlotProps => ({
  data: [],
  type: "swarm-box",
  config: {
    plotId: "new",
    labels: {
      title: "New Plot",
      x: "X Axis Label",
      y: "Y Axis Label",
    },
    plotClassName: "vivo-plot-modal",
    plotSize: "medium",
  },
  dataIsLoading: false,
});

const createPlotTitle = (
  plotType: plotTypes,
  columnsTitleCreator: (column: string) => string,
  xVariable: string,
  yVariable: string,
) => {
  if (plotType === "barplot") {
    if (!xVariable) {
      return "Title";
    }
    return `Count of ${columnsTitleCreator(xVariable)}`;
  }
  if (plotType === "swarm-box") {
    if (!xVariable || !yVariable) {
      return "Title";
    }
    return `${columnsTitleCreator(yVariable)} per ${columnsTitleCreator(
      xVariable,
    )}`;
  }
  if (plotType === "scatter") {
    if (!xVariable || !yVariable) {
      return "Title";
    }
    return `${columnsTitleCreator(yVariable)} by ${columnsTitleCreator(
      xVariable,
    )}`;
  }
  return "Title";
};

export const usePlotSetup = ({
  initialPlotSetup,
  columnsTitleCreator,
  columnOptions,
  globalFilters = {},
}: {
  initialPlotSetup: VivoPlotProps;
  columnsTitleCreator: (column: string) => string;
  columnOptions: ColumnOption[];

  globalFilters?: any;
}) => {
  const [plotTitle, setPlotTitle] = useState(
    initialPlotSetup?.config.labels.title || "",
  );
  const [plotSubtitle, setPlotSubtitle] = useState(
    initialPlotSetup?.config.labels.subtitle || "",
  );
  const [plotXAxisLabel, setPlotXAxisLabel] = useState(
    initialPlotSetup?.config.labels.x || "X Axis Label",
  );
  const [plotYAxisLabel, setPlotYAxisLabel] = useState(
    initialPlotSetup?.config.labels.y || "Y Axis Label",
  );
  const [plotType, setPlotType] = useState<plotTypes>(
    initialPlotSetup?.type || "swarm-box",
  );

  const [showXTicks, setShowXTicks] = useState(
    initialPlotSetup?.config.showXTicks ?? true,
  );
  const [showYTicks, setShowYTicks] = useState(
    initialPlotSetup?.config.showYTicks ?? true,
  );
  const [showXAxisLabel, setShowXAxisLabel] = useState(
    initialPlotSetup?.config.showXAxisLabel ?? true,
  );
  const [showYAxisLabel, setShowYAxisLabel] = useState(
    initialPlotSetup?.config.showYAxisLabel ?? true,
  );
  const [showSwarm, setShowSwarm] = useState(
    initialPlotSetup?.config.showSwarm || false,
  );
  const [showLegend, setShowLegend] = useState(
    initialPlotSetup?.config.showLegend || false,
  );
  const [isBoxplotInFront, setIsBoxplotInFront] = useState(
    initialPlotSetup?.config.isBoxplotInFront ?? true,
  );
  const [xVariable, setXVariable] = useState(initialPlotSetup?.x?.dataMapping);
  const [yVariable, setYVariable] = useState(initialPlotSetup?.y?.dataMapping);
  const [groupVariable, setGroupVariable] = useState(
    initialPlotSetup?.subgroup?.dataMapping || "none",
  );
  const [fillVariable, setFillVariable] = useState(
    initialPlotSetup?.fill?.dataMapping || "none",
  );
  const [plotSize, setPlotSize] = useState(
    initialPlotSetup?.config?.plotSize || "medium",
  );
  const [colorPalette, setColorPalette] = useState(
    initialPlotSetup?.config?.palette || "Blue",
  );
  const [xZeroAsMinValue, setXZeroAsMinValue] = useState(
    initialPlotSetup?.x?.zeroAsMinValue || false,
  );
  const [yZeroAsMinValue, setYZeroAsMinValue] = useState(
    initialPlotSetup?.y?.zeroAsMinValue || false,
  );
  const [xSortDirection, setXSortDirection] = useState(
    initialPlotSetup?.config?.xSortDirection || "none",
  );
  const [xSortBy, setXSortBy] = useState(() =>
    getEditorBarplotSortBy(initialPlotSetup),
  );
  const [isHorizontal, setIsHorizontal] = useState(
    initialPlotSetup?.config?.orientation === "horizontal" || false,
  );
  const [maxBarsDisplayed, setMaxBarsDisplayed] = useState(
    initialPlotSetup?.config?.maxBarCount || null,
  );

  const [plotFilters, setPlotFilters] = useState(
    initialPlotSetup?.plotFilters || {},
  );

  // ── Metric card state ──────────────────────────────────────────────────────
  const [metricDisplayStyle, setMetricDisplayStyle] =
    useState<MetricDisplayStyle>(
      initialPlotSetup?.config?.metricCard?.displayStyle ?? "number",
    );
  const [metricAggregation, setMetricAggregation] = useState<MetricAggregation>(
    initialPlotSetup?.config?.metricCard?.aggregation ?? "count",
  );
  const [metricValueColumn, setMetricValueColumn] = useState<
    string | undefined
  >(initialPlotSetup?.config?.metricCard?.valueColumn);
  const [metricUnit, setMetricUnit] = useState<string>(
    initialPlotSetup?.config?.metricCard?.unit ?? "",
  );
  const [metricThresholdWarn, setMetricThresholdWarn] = useState<
    number | undefined
  >(initialPlotSetup?.config?.metricCard?.thresholdWarn);
  const [metricThresholdCritical, setMetricThresholdCritical] = useState<
    number | undefined
  >(initialPlotSetup?.config?.metricCard?.thresholdCritical);
  const [metricThresholdDirection, setMetricThresholdDirection] = useState<
    "above" | "below"
  >(initialPlotSetup?.config?.metricCard?.thresholdDirection ?? "above");
  const [metricShowComparison, setMetricShowComparison] = useState<boolean>(
    initialPlotSetup?.config?.metricCard?.showComparison ?? false,
  );
  // Phase 2 — donut
  const [metricDenominatorSource, setMetricDenominatorSource] = useState<
    "total" | "filter" | "fixed"
  >(initialPlotSetup?.config?.metricCard?.denominatorSource ?? "total");
  const [metricDenominatorFixed, setMetricDenominatorFixed] = useState<
    number | undefined
  >(initialPlotSetup?.config?.metricCard?.denominatorFixed);
  // Phase 3 — sparkline
  const [metricTrendDateColumn, setMetricTrendDateColumn] = useState<
    string | undefined
  >(initialPlotSetup?.config?.metricCard?.trendDateColumn);
  const [metricTrendBucketSize, setMetricTrendBucketSize] = useState<
    "day" | "week" | "month"
  >(initialPlotSetup?.config?.metricCard?.trendBucketSize ?? "week");
  const [metricTrendPeriods, setMetricTrendPeriods] = useState<number[]>(
    initialPlotSetup?.config?.metricCard?.trendPeriods ?? [14, 30, 60, 90],
  );
  const [metricTrendDefaultPeriod, setMetricTrendDefaultPeriod] =
    useState<number>(
      initialPlotSetup?.config?.metricCard?.trendDefaultPeriod ?? 30,
    );

  // ── Line chart state ───────────────────────────────────────────────────────
  // Line is a time-series chart: X is almost always a date/month, so default to true.
  const [lineXIsDate, setLineXIsDate] = useState<boolean>(
    initialPlotSetup?.config?.lineChart?.xIsDate ?? true,
  );

  const plotId =
    initialPlotSetup?.config?.plotId !== "new"
      ? initialPlotSetup?.config?.plotId
      : uuidv4();

  const changePlotType = useCallback(
    (type) => {
      setPlotType(type);
      if (type === "barplot") {
        setPlotYAxisLabel(`Count of ${columnsTitleCreator(xVariable)}`);
      } else {
        setPlotYAxisLabel(columnsTitleCreator(yVariable));
        setIsHorizontal(false);
      }
    },

    [columnsTitleCreator, xVariable, yVariable],
  );

  const changeXVariable = useCallback(
    (variable) => {
      setXVariable(variable);
      setPlotXAxisLabel(columnsTitleCreator(variable));
      setPlotTitle(
        createPlotTitle(plotType, columnsTitleCreator, variable, yVariable),
      );
      if (plotType === "barplot") {
        setPlotYAxisLabel(`Count of ${columnsTitleCreator(variable)}`);
      }
    },
    [columnsTitleCreator, plotType, yVariable],
  );

  const changeYVariable = useCallback(
    (variable) => {
      setYVariable(variable);
      setPlotYAxisLabel(columnsTitleCreator(variable));
      setPlotTitle(
        createPlotTitle(plotType, columnsTitleCreator, xVariable, variable),
      );
    },
    [columnsTitleCreator, plotType, xVariable],
  );

  const setters = {
    setPlotTitle,
    setPlotSubtitle,
    setPlotXAxisLabel,
    setPlotYAxisLabel,
    setPlotType: changePlotType,

    setShowXTicks,
    setShowYTicks,
    setShowXAxisLabel,
    setShowYAxisLabel,
    setShowSwarm,
    setXVariable: changeXVariable,
    setYVariable: changeYVariable,
    setGroupVariable,
    setFillVariable,
    setPlotSize,
    setColorPalette,
    setXZeroAsMinValue,
    setYZeroAsMinValue,
    setXSortDirection,
    setXSortBy,
    setPlotFilters,
    setIsHorizontal,
    setMaxBarsDisplayed,
    setShowLegend,
    setIsBoxplotInFront,
    // metric card
    setMetricDisplayStyle,
    setMetricAggregation,
    setMetricValueColumn,
    setMetricUnit,
    setMetricThresholdWarn,
    setMetricThresholdCritical,
    setMetricThresholdDirection,
    setMetricShowComparison,
    setMetricDenominatorSource,
    setMetricDenominatorFixed,
    setMetricTrendDateColumn,
    setMetricTrendBucketSize,
    setMetricTrendPeriods,
    setMetricTrendDefaultPeriod,
    // line chart
    setLineXIsDate,
  };

  const getters = {
    plotTitle,
    plotSubtitle,
    plotXAxisLabel,
    plotYAxisLabel,
    plotType,

    showXTicks,
    showYTicks,
    showXAxisLabel,
    showYAxisLabel,
    showSwarm,
    xVariable,
    yVariable,
    groupVariable,
    fillVariable,
    plotSize,
    colorPalette,
    xZeroAsMinValue,
    yZeroAsMinValue,
    xSortDirection,
    xSortBy,
    plotFilters,
    hasFilterConflicts: detectFilterConflicts(globalFilters, plotFilters)
      .hasConflicts,
    isHorizontal,
    maxBarsDisplayed,
    showLegend,
    isBoxplotInFront,
    // metric card
    metricDisplayStyle,
    metricAggregation,
    metricValueColumn,
    metricUnit,
    metricThresholdWarn,
    metricThresholdCritical,
    metricThresholdDirection,
    metricShowComparison,
    metricDenominatorSource,
    metricDenominatorFixed,
    metricTrendDateColumn,
    metricTrendBucketSize,
    metricTrendPeriods,
    metricTrendDefaultPeriod,
    // line chart
    lineXIsDate,
  };

  const xMemo = useMemo(
    () => ({
      type: "categorical" as const,
      dataMapping: xVariable,
      dataType: columnOptions.find((column) => column.name === xVariable)
        ?.data_type,
      zeroAsMinValue: xZeroAsMinValue,
    }),
    [columnOptions, xVariable, xZeroAsMinValue],
  );

  const yMemo = useMemo(
    () => ({
      type: "continuous" as const,
      dataMapping: yVariable,

      zeroAsMinValue: yZeroAsMinValue,
    }),
    [yVariable, yZeroAsMinValue],
  );

  const configMemo = useMemo(
    () => ({
      palette: colorPalette,
      plotClassName: "vivo-plot-modal",
      labels: {
        title: plotTitle,
        subtitle: plotSubtitle,
        x: plotXAxisLabel,
        y: plotYAxisLabel,
      },
      plotSize: plotSize as "small" | "medium" | "large",
      orientation: isHorizontal ? "horizontal" : "vertical",
      xSortDirection,
      xSortBy,
      barplotSortSemanticsVersion: RENDERED_AXIS_SORT_VERSION,
      showXTicks,
      showYTicks,
      showXAxisLabel,
      showYAxisLabel,
      showSwarm,
      showLegend,
      isBoxplotInFront,
      plotId,
      maxBarCount: maxBarsDisplayed,
      ...(plotType === "line"
        ? {
            lineChart: {
              xField: xVariable,
              yField: yVariable,
              ...(groupVariable && groupVariable !== "none"
                ? { seriesField: groupVariable }
                : {}),
              // month_id (YYYYMM int) and datetime columns render on a time axis;
              // LineChartVisualization.parseX handles both when xIsDate is true.
              xIsDate: lineXIsDate,
              ...(showXAxisLabel ? { xLabel: plotXAxisLabel } : {}),
              ...(showYAxisLabel ? { yLabel: plotYAxisLabel } : {}),
            },
          }
        : {}),
      ...(plotType === "metric-card"
        ? {
            metricCard: {
              displayStyle: metricDisplayStyle,
              aggregation: metricAggregation,
              ...(metricValueColumn ? { valueColumn: metricValueColumn } : {}),
              ...(metricUnit ? { unit: metricUnit } : {}),
              ...(metricThresholdWarn !== undefined
                ? { thresholdWarn: metricThresholdWarn }
                : {}),
              ...(metricThresholdCritical !== undefined
                ? { thresholdCritical: metricThresholdCritical }
                : {}),
              thresholdDirection: metricThresholdDirection,
              showComparison: metricShowComparison,
              // Phase 2
              denominatorSource: metricDenominatorSource,
              ...(metricDenominatorFixed !== undefined
                ? { denominatorFixed: metricDenominatorFixed }
                : {}),
              // Phase 3
              ...(metricTrendDateColumn
                ? { trendDateColumn: metricTrendDateColumn }
                : {}),
              trendBucketSize: metricTrendBucketSize,
              trendPeriods: metricTrendPeriods,
              trendDefaultPeriod: metricTrendDefaultPeriod,
            },
          }
        : {}),
    }),
    [
      colorPalette,
      plotTitle,
      plotSubtitle,
      plotXAxisLabel,
      plotYAxisLabel,
      plotSize,
      showXTicks,
      showYTicks,
      xSortDirection,
      xSortBy,
      showXAxisLabel,
      showYAxisLabel,
      showSwarm,
      showLegend,
      isBoxplotInFront,
      plotId,
      isHorizontal,
      maxBarsDisplayed,
      plotType,
      // metric card
      metricDisplayStyle,
      metricAggregation,
      metricValueColumn,
      metricUnit,
      metricThresholdWarn,
      metricThresholdCritical,
      metricThresholdDirection,
      metricShowComparison,
      metricDenominatorSource,
      metricDenominatorFixed,
      metricTrendDateColumn,
      metricTrendBucketSize,
      metricTrendPeriods,
      metricTrendDefaultPeriod,
      // line chart
      xVariable,
      yVariable,
      groupVariable,
      lineXIsDate,
    ],
  );

  const subgroupMemo = useMemo(
    () => ({
      dataMapping: groupVariable,
      label: "group",
    }),
    [groupVariable],
  );
  const fillMemo = useMemo(
    () => ({
      dataMapping: fillVariable,
      label: "x",
    }),
    [fillVariable],
  );

  return {
    plot: {
      type: plotType,
      x: xMemo,
      y: yMemo,
      subgroup: subgroupMemo,
      fill: fillMemo,
      config: configMemo,
      plotFilters,
    },
    setters,
    getters,
  };
};
